import { canOpenResource } from './access';
import { policyAllows } from './modules';
import type { Context, Entity, Kind, Snapshot } from './types';
export const resourceForKind: Partial<Record<Kind, string>> = {
  device: 'device',
  binding: 'device',
  batch: 'collection',
  recording: 'recording',
  job: 'analysis',
  clue: 'clue',
  case: 'case',
  plan: 'plan',
  lesson: 'teaching',
  teacher: 'teacher',
  student: 'student',
  followUp: 'service',
  feedback: 'feedback',
  knowledge: 'knowledge',
  template: 'analysisTemplate',
  repair: 'repair',
  inventory: 'inventory',
  model: 'deviceModel',
  station: 'collection',
  correction: 'correction',
  evaluation: 'evaluation',
  organization: 'organization',
  policy: 'permission',
  rule: 'rule',
  modelConfig: 'model',
  integration: 'integration',
  subscription: 'subscription',
  audit: 'audit',
  ticket: 'ticket',
  help: 'help',
  pilot: 'pilot',
  position: 'location',
  zone: 'location',
};
export function canAccessEntity(
  state: Snapshot,
  row: Entity,
  basic: (row: Entity, context: Context) => boolean,
  seen = new Set<string>(),
): boolean {
  if (
    seen.has(row.uuid) ||
    !basic(row, state.context) ||
    !policyAllows(state, row.kind, 'read')
  )
    return false;
  const resource = resourceForKind[row.kind];
  if (resource && !canOpenResource(resource, state)) return false;
  seen.add(row.uuid);
  const inherits = [
    'task',
    'message',
    'audit',
    'job',
    'clue',
    'case',
    'followUp',
    'feedback',
    'correction',
  ].includes(row.kind);
  const publishedCase =
    row.kind === 'knowledge' &&
    (row.status === 'published' ||
      (row.status === 'withdrawn' && row.fields?.learningManaged === 'true'));
  const refs = [
    inherits ? row.sourceUuid : undefined,
    publishedCase ? undefined : row.evidence?.recordingUuid,
  ].filter((x): x is string => !!x);
  try {
    const origins: unknown = JSON.parse(row.fields?.reviewSourceUuids || '[]');
    if (
      !Array.isArray(origins) ||
      origins.some((value) => typeof value !== 'string')
    )
      return false;
    refs.push(...origins);
  } catch {
    return false;
  }
  if (
    (row.kind === 'job' && row.fields?.analysisTask) ||
    (row.kind === 'evaluation' && row.fields?.qualityReport)
  ) {
    try {
      const ids: unknown = JSON.parse(row.fields?.sourceRecordingUuids || '[]');
      if (
        !Array.isArray(ids) ||
        !ids.length ||
        ids.some((id) => typeof id !== 'string')
      )
        return false;
      refs.push(...ids);
    } catch {
      return false;
    }
  }
  for (const uuid of new Set(refs)) {
    const target = state.entities.find((e) => e.uuid === uuid);
    if (!target || !canAccessEntity(state, target, basic, new Set(seen)))
      return false;
  }
  return true;
}
